import type { SortOrder } from './types';

/**
 * The one query contract every DataTable-backed list speaks.
 *
 * The canonical names are `page` / `limit` / `sort` / `order` / `search`, plus
 * whatever filters the table declares. They are canonical because the V2
 * endpoints already agree on them - `/v2/billing/*`, `/v2/practices` and
 * `/v2/practices/calls` all read exactly these - so a table that emits this
 * shape is asking every one of them the same way.
 *
 * Divergent endpoints are handled at the edge with `adaptDataTableParams`
 * rather than by bending the hook; see that function for the worked example.
 */

/**
 * What a filter value may be. Deliberately narrow: these end up on a query
 * string, so anything that isn't a scalar or a list of scalars has no
 * serialisation the backend agrees on.
 */
export type DataTableFilterValue = string | number | boolean | readonly string[] | null | undefined;

/**
 * A table's filter state, keyed by the query-param name the endpoint expects.
 *
 * Declare your own as a **type alias**, not an `interface`: only aliases pick
 * up TypeScript's implicit index signature, so an interface will not satisfy
 * this constraint.
 *
 *     type InvoiceFilters = { status: string[]; practiceId: string[] };
 */
export type DataTableFilterMap = { [name: string]: DataTableFilterValue };

/**
 * The canonical params object, ready to hand to an HTTP client as a query
 * bag. Empty values (`undefined`, `null`, `''`, `[]`) are omitted entirely, so
 * the query string stays clean and two tables in the same state produce the
 * same cache key.
 *
 * `page`, `limit`, `sort` and `order` are always present; `search` and the
 * filters only when they hold something. Filters are typed loosely here (they
 * are heading for a query string either way) - read them back through
 * `table.filters`, which stays exactly typed.
 */
export type DataTableParams<K extends string = string> = {
    page: number;
    limit: number;
    sort?: K;
    order?: SortOrder;
    search?: string;
} & DataTableFilterMap;

/** True for values that must not reach the query string. */
function isEmptyParam(value: DataTableFilterValue): boolean {
    if (value === undefined || value === null || value === '') {
        return true;
    }
    return Array.isArray(value) && value.length === 0;
}

/**
 * Drops the empty entries from a filter map. Exported for the hook; useful on
 * its own when an app assembles extra params alongside the table's.
 */
export function compactParams(
    values: Readonly<Record<string, DataTableFilterValue>>
): Readonly<Record<string, DataTableFilterValue>> {
    return Object.fromEntries(Object.entries(values).filter(([, value]) => !isEmptyParam(value)));
}

/**
 * Renames canonical keys for an endpoint that disagrees with the convention.
 *
 * This is the whole adapter story on purpose: an endpoint that differs by more
 * than its parameter *names* differs in its model, and wants its own hook, not
 * a translation layer.
 *
 * The worked example is the work queue. `GET /v2/work-queue` takes
 * `sortBy`/`dir` where everything else takes `sort`/`order`:
 *
 *     adaptDataTableParams(table.params, { sort: 'sortBy', order: 'dir' })
 *     // { page, limit, sortBy, dir, search?, ...filters }
 *
 * Work queue is **not** migrated to `useDataTable`, and this is not a live
 * call site - its grouped, cursor-ish infinite list keeps its own state. It is
 * here as the shape an adapter takes when a real one is needed.
 *
 * @param params canonical params, typically `table.params`
 * @param rename canonical key -> endpoint key. Keys not listed pass through.
 */
export function adaptDataTableParams(
    params: Readonly<Record<string, DataTableFilterValue>>,
    rename: Readonly<Record<string, string>>
): Readonly<Record<string, DataTableFilterValue>> {
    return Object.fromEntries(
        Object.entries(params).map(([key, value]) => [rename[key] ?? key, value])
    );
}
